import React, {
  useState,
  useMemo,
  useRef,
  useEffect,
  useCallback,
} from "react";
import { Users, Search, ArrowUpDown, X, Check, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import PlayerListItem from "./PlayerListItem";
import Button from "components/common/Button";
import { positionLabel } from "v2/constants/positions";

const SORT_OPTIONS = [
  { value: "name-asc", labelKey: "team.playerList.sort.nameAsc" },
  { value: "name-desc", labelKey: "team.playerList.sort.nameDesc" },
  { value: "position-asc", labelKey: "team.playerList.sort.positionAsc" },
  { value: "position-desc", labelKey: "team.playerList.sort.positionDesc" },
  { value: "load-desc", labelKey: "team.playerList.sort.loadDesc" },
  { value: "load-asc", labelKey: "team.playerList.sort.loadAsc" },
];

const PlayerListPanel = ({
  players,
  totalCount,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
  selectedPlayerId,
  onPlayerSelect,
  onAddPlayer,
  hideStats = false,
  onSortedPlayersChange,
}) => {
  const { t } = useTranslation();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortKey, setSortKey] = useState("name-asc");
  const [sortOpen, setSortOpen] = useState(false);
  const searchInputRef = useRef(null);
  const sortRef = useRef(null);
  const playerRefsMap = useRef({});

  const setPlayerRef = useCallback((playerId, el) => {
    if (el) {
      playerRefsMap.current[playerId] = el;
    } else {
      delete playerRefsMap.current[playerId];
    }
  }, []);

  // Focus input when search opens
  useEffect(() => {
    if (searchOpen) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  }, [searchOpen]);

  // Close sort dropdown on outside click or Escape
  useEffect(() => {
    if (!sortOpen) return;

    const handleClickOutside = (e) => {
      if (sortRef.current && !sortRef.current.contains(e.target)) {
        setSortOpen(false);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setSortOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [sortOpen]);

  const handleCloseSearch = () => {
    setSearchOpen(false);
    setSearchTerm("");
  };

  // Search and non-default sorts operate client-side over the loaded pages,
  // so they'd silently miss unloaded athletes — pull the rest in first.
  useEffect(() => {
    if (!hasMore || loadingMore) return;
    if (searchTerm.trim() || sortKey !== "name-asc") onLoadMore?.();
  }, [searchTerm, sortKey, hasMore, loadingMore, onLoadMore]);

  // Infinite scroll: load the next page when the sentinel below the list
  // scrolls into view.
  const sentinelObserver = useRef(null);
  const sentinelRef = useCallback(
    (node) => {
      if (sentinelObserver.current) sentinelObserver.current.disconnect();
      if (!node || !hasMore) return;
      sentinelObserver.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && !loadingMore) onLoadMore?.();
      });
      sentinelObserver.current.observe(node);
    },
    [hasMore, loadingMore, onLoadMore]
  );

  // Scroll to selected player and clear search if it hides them
  useEffect(() => {
    if (!selectedPlayerId) return;

    // Check if the selected player is hidden by the current search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      const selectedPlayer = players.find((p) => p.id === selectedPlayerId);
      if (
        selectedPlayer &&
        !selectedPlayer.name.toLowerCase().includes(term) &&
        !(
          selectedPlayer.position &&
          positionLabel(selectedPlayer.position).toLowerCase().includes(term)
        )
      ) {
        setSearchTerm("");
      }
    }

    // Delay to allow DOM to update after filter clear
    const timer = setTimeout(() => {
      const el = playerRefsMap.current[selectedPlayerId];
      if (el) {
        el.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    }, 50);
    return () => clearTimeout(timer);
  }, [selectedPlayerId, players]);

  // Filter and sort players
  const filteredPlayers = useMemo(() => {
    let result = players;
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (p) =>
          p.name.toLowerCase().includes(term) ||
          (p.position && positionLabel(p.position).toLowerCase().includes(term))
      );
    }

    const [field, direction] = sortKey.split("-");
    const asc = direction === "asc";

    return [...result].sort((a, b) => {
      if (field === "name") {
        return asc
          ? a.name.localeCompare(b.name)
          : b.name.localeCompare(a.name);
      }
      if (field === "position") {
        const posA = positionLabel(a.position || "").toLowerCase();
        const posB = positionLabel(b.position || "").toLowerCase();
        return asc ? posA.localeCompare(posB) : posB.localeCompare(posA);
      }
      if (field === "load") {
        const loadA = a.seasonLoad ?? 0;
        const loadB = b.seasonLoad ?? 0;
        return asc ? loadA - loadB : loadB - loadA;
      }
      return 0;
    });
  }, [players, searchTerm, sortKey]);

  // Report sorted/filtered player list to parent for keyboard navigation
  useEffect(() => {
    onSortedPlayersChange?.(filteredPlayers);
  }, [filteredPlayers, onSortedPlayersChange]);

  const sortActive = sortKey !== "name-asc";

  return (
    <div className="flex h-full flex-col rounded-xl border border-[#F3F4F6] bg-white">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-[#F3F4F6] px-4 py-3">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-[#111827]">
            {t("team.playerList.title")}
          </h3>
          <span className="rounded-full bg-[#F3F4F6] px-2 py-0.5 text-xs font-medium text-[#6B7280]">
            {totalCount ?? players.length}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          {players.length > 0 && (
            <>
              <div className="relative" ref={sortRef}>
                <button
                  onClick={() => setSortOpen((prev) => !prev)}
                  className={`flex h-7 w-7 items-center justify-center rounded-md border transition duration-150 ${
                    sortActive || sortOpen
                      ? "border-[#E07A3A] bg-[#FEF7F3] text-[#E07A3A]"
                      : "border-[#E5E7EB] bg-white text-[#6B7280] hover:border-[#D1D5DB] hover:bg-[#F9FAFB] hover:text-[#374151]"
                  }`}
                  title={t("team.playerList.sortTitle")}
                >
                  <ArrowUpDown size={14} />
                </button>
                {sortOpen && (
                  <div
                    className="absolute right-0 top-full z-10 mt-1 w-40 overflow-hidden rounded-lg border border-[#E5E7EB] bg-white shadow-lg"
                    style={{
                      animation: "dropdownFadeIn 150ms ease-out",
                    }}
                  >
                    {SORT_OPTIONS.map((opt) => {
                      const selected = sortKey === opt.value;
                      return (
                        <button
                          key={opt.value}
                          onClick={() => {
                            setSortKey(opt.value);
                            setSortOpen(false);
                          }}
                          className={`flex w-full items-center justify-between px-3 py-2 text-xs transition duration-150 ${
                            selected
                              ? "bg-[#FEF3E7] font-medium text-[#E07A3A]"
                              : "text-[#374151] hover:bg-[#F9FAFB]"
                          }`}
                        >
                          {t(opt.labelKey)}
                          {selected && <Check size={12} />}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              <button
                onClick={() =>
                  searchOpen ? handleCloseSearch() : setSearchOpen(true)
                }
                className={`flex h-7 w-7 items-center justify-center rounded-md border transition duration-150 ${
                  searchOpen
                    ? "border-[#E07A3A] bg-[#FEF7F3] text-[#E07A3A]"
                    : "border-[#E5E7EB] bg-white text-[#6B7280] hover:border-[#D1D5DB] hover:bg-[#F9FAFB] hover:text-[#374151]"
                }`}
                title={t("team.playerList.searchTitle")}
              >
                <Search size={14} />
              </button>
            </>
          )}
          <Button onClick={onAddPlayer} size="sm">
            {t("team.playerList.addAthlete")}
          </Button>
        </div>
      </div>

      {/* Expandable Search Bar */}
      <div
        className="overflow-hidden transition-all duration-200"
        style={{
          maxHeight: searchOpen ? "52px" : "0px",
          opacity: searchOpen ? 1 : 0,
        }}
      >
        <div className="relative border-b border-[#F3F4F6] px-4 py-2">
          <Search
            size={14}
            className="absolute left-7 top-1/2 -translate-y-1/2 text-[#9CA3AF]"
          />
          <input
            ref={searchInputRef}
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t("team.playerList.searchPlaceholder")}
            className="w-full rounded-lg border border-[#E5E7EB] py-1.5 pl-8 pr-8 text-xs text-[#111827] placeholder-[#9CA3AF] outline-none transition-all duration-150 focus:border-[#E07A3A] focus:shadow-[0_0_0_3px_rgba(224,122,58,0.1)]"
            onKeyDown={(e) => {
              if (e.key === "Escape") handleCloseSearch();
            }}
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm("")}
              className="absolute right-7 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#6B7280]"
            >
              <X size={12} />
            </button>
          )}
        </div>
      </div>

      {/* Scrollable Player List */}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {players.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-6 py-8">
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-[#F3F4F6] to-[#E5E7EB]">
              <Users size={22} className="text-[#6B7280]" />
            </div>
            <h3 className="mb-1 text-sm font-semibold text-[#111827]">
              {t("team.playerList.emptyTitle")}
            </h3>
            <p className="mb-3 max-w-[200px] text-center text-xs leading-relaxed text-[#6B7280]">
              {t("team.playerList.emptyBody")}
            </p>
            <Button onClick={onAddPlayer} size="sm">
              {t("team.playerList.addAthlete")}
            </Button>
          </div>
        ) : filteredPlayers.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-6 py-8">
            <Search size={20} className="mb-2 text-[#9CA3AF]" />
            <p className="text-xs text-[#6B7280]">
              {t("team.playerList.noMatches", { term: searchTerm })}
            </p>
          </div>
        ) : (
          <>
            {filteredPlayers.map((player) => (
              <PlayerListItem
                key={player.id}
                ref={(el) => setPlayerRef(player.id, el)}
                player={player}
                isSelected={player.id === selectedPlayerId}
                onClick={onPlayerSelect}
                hideStats={hideStats}
              />
            ))}
            {hasMore && (
              <div
                ref={sentinelRef}
                className="flex items-center justify-center py-3"
              >
                <Loader2 size={16} className="animate-spin text-[#E07A3A]" />
              </div>
            )}
          </>
        )}
      </div>

      {/* Dropdown animation keyframes */}
      <style>{`
        @keyframes dropdownFadeIn {
          from {
            opacity: 0;
            transform: translateY(-4px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default PlayerListPanel;
